import type { Audit, PaginatedAuditResponse } from '@/api/admin/audit';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface AuditModalProps {
  open: boolean;
  onClose: () => void;
  userName: string;
  auditData: PaginatedAuditResponse | undefined;
  currentPage: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
}

// Field label mapping (Portuguese)
const fieldLabel: Record<string, string> = {
  name: 'nome',
  email: 'e-mail',
  profile: 'perfil',
  active: 'status',
  external: 'usuário externo',
  locked_control_code: 'permissões',
  lock: 'bloqueio',
  password: 'senha',
  image: 'imagem',
  temporary_password: 'senha temporária',
  cpf: 'CPF',
  username: 'usuário',
};

// Format field values for display
function formatValue(value: unknown, field?: string): string {
  if (typeof value === 'boolean') {
    if (field === 'active') return value ? 'ativo' : 'inativo';
    return value ? 'sim' : 'não';
  }

  if (value === null || value === undefined) {
    return '';
  }

  if (Array.isArray(value)) {
    // Handle profile array
    if (field === 'profile' && value.length > 0 && typeof value[0] === 'object') {
      return value.map((p: { name?: string }) => p?.name || String(p)).join(', ');
    }
    return value.join(', ');
  }

  if (typeof value === 'object') {
    // Handle image object
    if (field === 'image' && 'key' in (value as Record<string, unknown>)) {
      return (value as { key: string }).key || '';
    }
    // Handle profile object
    if (field === 'profile' && 'name' in (value as Record<string, unknown>)) {
      return (value as { name: string }).name || '';
    }
    return '';
  }

  return String(value);
}

// Format audit action for display
function formatAction(audit: Audit): React.ReactNode {
  const { type, metadata } = audit;

  if (type === 'create' || !metadata || metadata.length === 0) {
    return audit.action;
  }

  // Check for temporary password reset
  const isTempReset = metadata.some(
    (m) => m.field === 'temporary_password' && formatValue(m.after, 'temporary_password') === 'sim',
  );

  return (
    <div className="space-y-2">
      {metadata.map((m) => {
        const field = m.field;
        const pretty = fieldLabel[field] ?? field;
        const before = formatValue(m.before, field);
        const after = formatValue(m.after, field);

        // Handle lock field
        if (field === 'lock') {
          if (after === 'sim') {
            return (
              <div key={`${field}-${after}`} className="text-sm">
                Usuário <span className="font-bold">bloqueado</span>
              </div>
            );
          }
          if (after === 'não') {
            return (
              <div key={`${field}-${after}`} className="text-sm">
                Usuário <span className="font-bold">desbloqueado</span>
              </div>
            );
          }
        }

        // Handle password field
        if (field === 'password') {
          if (isTempReset) return null;
          return (
            <div key={field} className="text-sm">
              Senha alterada.
            </div>
          );
        }

        // Handle image field
        if (field === 'image') {
          if (isTempReset) return null;
          return (
            <div key={field} className="text-sm">
              Imagem alterada.
            </div>
          );
        }

        // Handle temporary_password field
        if (field === 'temporary_password' && after === 'sim') {
          return (
            <div key={field} className="text-sm">
              Reset de senha solicitado.
            </div>
          );
        }

        // Handle active field
        if (field === 'active') {
          if (after === 'ativo') {
            return (
              <div key={`${field}-${after}`} className="text-sm">
                Usuário <span className="font-bold">ativado</span>
              </div>
            );
          }
          if (after === 'inativo') {
            return (
              <div key={`${field}-${after}`} className="text-sm">
                Usuário <span className="font-bold">inativado</span>
              </div>
            );
          }
        }

        // Default field change display
        const renderValue = (value: string) => {
          if (pretty === 'perfil') {
            return (
              <span className="font-medium flex flex-wrap">
                {value.split(',').map((part, idx) => (
                  <span key={`${part.trim()}-${idx}`}>
                    {part.trim()}
                    {idx < value.split(',').length - 1 && ',\u00A0'}
                  </span>
                ))}
              </span>
            );
          }
          return <span className="font-medium break-words">{value}</span>;
        };

        return (
          <div key={`${field}-${before}-${after}`} className="text-sm">
            <span className="mr-1 font-bold break-all">{`Campo '${pretty}' de`}</span>
            <span className="max-w-[20ch] whitespace-normal break-words">
              {renderValue(before)}
            </span>
            <span className="mx-1 font-bold">para</span>
            {renderValue(after)}
          </div>
        );
      })}
    </div>
  );
}

// Format date for display
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(date);
}

function AuditTableSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }, (_, i) => `skeleton-${i}`).map((key) => (
        <div key={key} className="flex gap-4">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 flex-1" />
        </div>
      ))}
    </div>
  );
}

export function AuditModal({
  open,
  onClose,
  userName,
  auditData,
  currentPage,
  onPageChange,
  isLoading,
}: AuditModalProps) {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Histórico de Alterações - {userName}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <AuditTableSkeleton />
            </div>
          ) : auditData && auditData.docs.length > 0 ? (
            <div className="space-y-4">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[150px]">Data/Hora</TableHead>
                      <TableHead className="w-[150px]">Usuário</TableHead>
                      <TableHead>Ação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditData.docs.map((audit) => (
                      <TableRow key={audit.id}>
                        <TableCell className="text-sm text-gray-500">
                          {formatDate(audit.created_at)}
                        </TableCell>
                        <TableCell className="text-sm">{audit.user?.name ?? ''}</TableCell>
                        <TableCell>{formatAction(audit)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={auditData.pages || 1}
                totalItems={auditData.total || 0}
                limit={auditData.limit || 10}
                onPageChange={onPageChange}
              />
            </div>
          ) : (
            <div className="flex justify-center items-center h-64 text-gray-500">
              Nenhum registro de alteração encontrado.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
